import cookies from 'js-cookie';

import {GOOGLE_ANALYTICS_ID} from '../../shared/config/config';

import initGoogleTracking from './initGoogleTracking';

export const GA_CATEGORIES = {
  CLOSE_ACCOUNT: 'close_account_flow',
  CONTACT_FORM: 'contact-form',
  REPORT_CONTENT: 'report-content',
};

class Analytics {
  experimentCookie = 'sp_exp_ga';
  trackingID = GOOGLE_ANALYTICS_ID;
  inited = false;
  hasTrackedFirstPageView: boolean = false;
  market?: string;

  initTracking(market: string) {
    if (this.inited) {
      return;
    }
    this.inited = true;
    this.market = market;

    /* eslint-disable */
    // https://developers.google.com/analytics/devguides/collection/analyticsjs/#alternative_async_tracking_snippet
    window.ga =
      window.ga ||
      function() {
        (window.ga.q = window.ga.q || []).push(arguments);
      };
    window.ga.l = +new Date();
    window.ga.q = [];
    /* eslint-enable */
    window.dataLayer = [];
  }

  /* Extra-async tracking:
    We want to set dimensions based on user product.
    Once user data is fetched (or failed since anonymous), this function is called,
    and the setup and dimensions are added to the beginning of the ga queue,
    that might already contain pageview and event calls - and these should include the dimensions.
   */
  setupUserProductForTracking(product: string | null) {
    if (!this.inited) throw new Error('Analytics not inited yet!');

    window.ga.q = [
      ['create', this.trackingID, 'auto', 'supportsite'],
      ['supportsite.require', 'linkid', {levels: 5}],
      [
        'supportsite.set',
        {
          dimension1: this.market,
          dimension2: product ? '1' : '0', // Logged in: 1, logged out: 0
          dimension5: 'support-site-frontend',
          dimension6: product || '',
        },
      ],
    ].concat(window.ga.q);

    // Once we got all the dimension data we need, we can trigger GA to start sending pageviews
    initGoogleTracking();
  }

  trackPageView(page = '') {
    if (!this.inited) throw new Error('Analytics not inited yet!');

    // replaces "/ca-fr/" with "/" for instance
    const pageWithoutMarket = page.replace(/^\/\w{2}(-\w{2})?\//, '/');

    window.ga('supportsite.set', {page: pageWithoutMarket});
    window.ga('supportsite.send', 'pageview');

    if (!this.hasTrackedFirstPageView) {
      this.hasTrackedFirstPageView = true;
    } else {
      // First pageview is not sent to GTM, since it triggers a "Normal" pageview on load
      window.dataLayer.push({
        event: 'virtualPageview',
        market: this.market,
        virtualPageURL: pageWithoutMarket,
        virtualPageTitle: document.title,
      });
    }

    this.trackGlobalExperiments();
  }

  trackEvent({category = '', action = '', label = '', value = 0, nonInteraction = false}) {
    if (!this.inited) throw new Error('Analytics not inited yet!');

    window.ga('supportsite.send', {
      hitType: 'event',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
      nonInteraction,
    });

    // Also send all events to GTM
    window.dataLayer.push({
      event: 'genericEvent',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
      nonInteraction,
    });
  }

  setDimension(dimension: number, value: string) {
    if (!this.inited) throw new Error('Analytics not inited yet!');

    window.ga('supportsite.set', `dimension${dimension}`, value);
  }

  // These experiments are shared with supportsite (PHP) through a cookie
  // Once we've migrated entirely, this will no longer be set, and can be removed
  trackGlobalExperiments() {
    const experiments: {key: string} = cookies.getJSON(this.experimentCookie);

    if (experiments) {
      Object.entries(experiments).forEach(([expName, expGroup]) => {
        this.trackExperiment(expName, expGroup);
      });
    }
  }

  trackExperiment(action: string, label: string) {
    this.trackEvent({
      category: 'experiments',
      action,
      label,
      nonInteraction: true,
    });
  }
}

export default new Analytics();
